import React, { useCallback, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  LinearProgress,
  Alert,
  Chip,
  Collapse,
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  Description,
  InsertDriveFile,
  Info,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { projectsApi } from '../services/api';

interface DocumentUploadProps {
  projectId: string;
  onUploadComplete?: () => void;
  onUploadStart?: () => void;
  onUploadProgress?: (progress: number) => void;
  documentType?: 'compliance' | 'submission';
  title?: string;
  description?: string;
  showHelperText?: boolean;
}

interface FileWithPreview {
  id: string;
  file: File;
  preview?: string;
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const getFileIcon = (filename: string | undefined) => {
  if (!filename) {
    return <InsertDriveFile sx={{ color: 'text.secondary' }} />;
  }

  const extension = filename.split('.').pop()?.toLowerCase();
  switch (extension) {
    case 'pdf':
      return <Description sx={{ color: 'secondary.main' }} />;
    case 'doc':
    case 'docx':
      return <InsertDriveFile sx={{ color: 'primary.main' }} />;
    default:
      return <InsertDriveFile sx={{ color: 'text.secondary' }} />;
  }
};

export default function DocumentUpload({
  projectId,
  onUploadComplete,
  onUploadStart,
  onUploadProgress,
  documentType = 'compliance',
  title,
  description,
  showHelperText = true
}: DocumentUploadProps) {
  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showInstructions, setShowInstructions] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [progressToReport, setProgressToReport] = useState<number | null>(null);

  // Handle upload completion callback asynchronously to avoid state update during render
  useEffect(() => {
    if (uploadComplete) {
      const timeoutId = setTimeout(() => {
        onUploadComplete?.();
        setUploadComplete(false);
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [uploadComplete, onUploadComplete]);

  // Handle progress updates asynchronously to avoid state update during render
  useEffect(() => {
    if (progressToReport !== null) {
      const timeoutId = setTimeout(() => {
        onUploadProgress?.(progressToReport);
        setProgressToReport(null);
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [progressToReport, onUploadProgress]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setError(null);
    const filesWithId = acceptedFiles.map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      file: file,
    }));
    setFiles((prev) => [...prev, ...filesWithId]);
  }, []);

  // Configure accepted file types based on document type
  const submissionTypes = {
    'application/pdf': ['.pdf'],
    'application/x-autocad': ['.dwg'],
  };

  const complianceTypes = {
    'application/pdf': ['.pdf'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'text/plain': ['.txt'],
    'text/markdown': ['.md'],
  };

  const allowedTypes = documentType === 'submission' ? submissionTypes : complianceTypes;

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: allowedTypes,
    maxSize: 200 * 1024 * 1024, // 200MB
    multiple: true,
  });

  const removeFile = (fileId: string) => {
    setFiles((prev) => prev.filter((file) => file.id !== fileId));
  };

  const uploadFiles = async () => {
    if (files.length === 0) {
      setError('Please select files to upload');
      return;
    }

    setUploading(true);
    setError(null);
    setUploadProgress(0);
    onUploadStart?.();

    try {
      const fileObjects = files.map((fileWithId) => fileWithId.file);

      // Simulate progress - in a real app, you'd want proper upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          const newProgress = prev + 10;
          setProgressToReport(newProgress);
          return newProgress;
        });
      }, 200);

      await (documentType === 'submission'
        ? projectsApi.uploadSubmissionDocuments(projectId, fileObjects)
        : projectsApi.uploadDocuments(projectId, fileObjects));

      clearInterval(progressInterval);
      setUploadProgress(100);
      setProgressToReport(100);

      // Clear files and set completion flag
      setTimeout(() => {
        setFiles([]);
        setUploadProgress(0);
        setUploadComplete(true);
      }, 1000);

    } catch (err: any) {
      console.error('Upload error:', err);
      setError(err.response?.data?.detail || 'Failed to upload files. Please try again.');
    } finally {
      setTimeout(() => {
        setUploading(false);
      }, 1000);
    }
  };

  return (
    <Box>
      {/* Collapsible Document Upload Instructions */}
      {showHelperText && (
        <Box sx={{ mb: 3 }}>
          <Button
            variant="text"
            startIcon={<Info />}
            endIcon={showInstructions ? <ExpandLess /> : <ExpandMore />}
            onClick={() => setShowInstructions(!showInstructions)}
            sx={{
              color: 'text.secondary',
              fontWeight: 500,
              textTransform: 'none',
              p: 0,
              minHeight: 'auto',
              '&:hover': { bgcolor: 'transparent', color: 'primary.main' }
            }}
          >
            Need help finding documents?
          </Button>

          <Collapse in={showInstructions}>
            <Alert
              severity="info"
              icon={<Info />}
              sx={{ mt: 2 }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Upload your project's compliance documents to ensure you meet all local requirements.
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Common documents include:
              </Typography>
              <Box component="ul" sx={{ mt: 0, mb: 1, pl: 2 }}>
                <li>Zoning codes and land use regulations</li>
                <li>Subdivision and development standards</li>
                <li>Building setback requirements</li>
                <li>Environmental impact guidelines</li>
                <li>Special overlay district rules</li>
              </Box>
              <Typography variant="body2">
                💡 <strong>Tip:</strong> Contact your local planning department or visit{' '}
                <Box
                  component="a"
                  href="https://ecode360.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: 'primary.main',
                    textDecoration: 'underline',
                    fontWeight: 500
                  }}
                >
                  ecode360.com
                </Box>
                {' '}(many municipalities use this site) to identify which specific ordinances apply to your project location.
              </Typography>
            </Alert>
          </Collapse>
        </Box>
      )}

      {/* Upload Area */}
      <Card
        elevation={0}
        sx={{
          border: 2,
          borderStyle: 'dashed',
          borderColor: isDragActive ? 'primary.main' : 'grey.300',
          bgcolor: isDragActive ? 'action.hover' : 'background.paper',
          transition: 'all 0.2s ease',
          mb: 3,
        }}
      >
        <CardContent>
          <Box
            {...getRootProps()}
            sx={{
              textAlign: 'center',
              py: 4,
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            <input {...getInputProps()} />
            <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
              {isDragActive ? 'Drop files here' : (title || `Upload ${documentType === 'submission' ? 'Submission' : 'Ordinance'} Documents`)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {description || 'Drag and drop files here, or click to select files'}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
              {documentType === 'submission' ? (
                <>
                  <Chip label="PDF" size="small" variant="outlined" />
                  <Chip label="DWG" size="small" variant="outlined" />
                </>
              ) : (
                <>
                  <Chip label="PDF" size="small" variant="outlined" />
                  <Chip label="Word" size="small" variant="outlined" />
                  <Chip label="Text" size="small" variant="outlined" />
                </>
              )}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              Maximum file size: 200MB per file
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* File List */}
      {files.length > 0 && (
        <Card elevation={0} sx={{ border: 1, borderColor: 'grey.300', mb: 3 }}>
          <CardContent sx={{ pb: 1 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Selected Files ({files.length})
            </Typography>
            <List dense>
              {files.map((fileWithId) => (
                <ListItem key={fileWithId.id} sx={{ px: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                    {getFileIcon(fileWithId.file.name)}
                  </Box>
                  <ListItemText
                    primary={fileWithId.file.name}
                    secondary={formatFileSize(fileWithId.file.size)}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={() => removeFile(fileWithId.id)}
                      disabled={uploading}
                      size="small"
                    >
                      <Delete />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Upload Progress */}
      {uploading && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Uploading files... {uploadProgress}%
          </Typography>
          <LinearProgress variant="determinate" value={uploadProgress} />
        </Box>
      )}

      {/* Upload Button */}
      {files.length > 0 && (
        <Button
          variant="contained"
          size="large"
          onClick={uploadFiles}
          disabled={uploading}
          startIcon={<CloudUpload />}
          sx={{ fontWeight: 600, px: 4 }}
        >
          {uploading ? 'Uploading...' : `Upload ${files.length} File${files.length > 1 ? 's' : ''}`}
        </Button>
      )}
    </Box>
  );
}